import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { setOperationStatus, UI_OPERATION_STATUS_ERROR, UI_OPERATION_STATUS_REQUEST } from '@uncinc/react-drupal-core';
import { Container } from '@uncinc/uncinc-react-kitchen-sink';

import { CoreLink, Main } from '../../components';
import Page from '../../containers/Page';
import useClasses from '../../hooks/useClasses';
import useRoutes from '../../hooks/useRoutes';
import useOperationStatus from '../../hooks/useOperationStatus';
import useToast from '../../hooks/useToast';
import OnboardingForm from './Form';
import OnboardingFormHti from './FormHti';

import './style.scss';

const PageOnboarding: React.FC = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const { onboarding } = useRoutes();
  const { buildClass, joinClasses } = useClasses('PageOnboarding', 'PageOnboarding--default');
  const toast = useToast('error');
  const dispatch = useDispatch();

  const registerOperationStatus = useOperationStatus('user', 'register');
  const loginOperationStatus = useOperationStatus('user', 'login');

  const hti = useMemo(() => {
    // Old way of using HTI (register user).
    const params = new URLSearchParams(search);
    return params.get('hti') !== null;
  }, [search]);

  useEffect(() => {
    if ([registerOperationStatus, loginOperationStatus].includes(UI_OPERATION_STATUS_ERROR)) {
      toast.show(t('pages.onboarding_credentials_default.error'));
    }

    // Consume the operation status so the toast won't show again.
    if (registerOperationStatus !== UI_OPERATION_STATUS_REQUEST) {
      dispatch(setOperationStatus('user', 'register', null));
    }
    if (loginOperationStatus !== UI_OPERATION_STATUS_REQUEST) {
      dispatch(setOperationStatus('user', 'login', null));
    }
  }, [registerOperationStatus, loginOperationStatus, t, toast, dispatch]);

  return (
    <Page className={buildClass()} hideMenuMain title={t('pages.onboarding_credentials_default.title')}>
      <Container>
        <div className="row">
          <div className="col-md-8 col-lg-6 offset-md-2 offset-lg-3 py-4 py-md-12">
            <header className={buildClass('header')}>
              <CoreLink
                to={onboarding.checklist}
                title={t('buttons.back')}
                className="icon-before icon-chevron icon-orientation-left"
              />
            </header>

            <Main className={buildClass('content')}>
              <h1 className={buildClass('title')}>
                {t('pages.onboarding_credentials_default.title')}
              </h1>
              <p className={joinClasses(buildClass('body'), 'body--large')}>
                {t('pages.onboarding_credentials_default.body')}
              </p>

              {hti ? (
                <OnboardingFormHti />
              ) : (
                <OnboardingForm />
              )}
            </Main>
          </div>
        </div>
      </Container>
    </Page>
  );
};

export default PageOnboarding;
